import clsx from 'clsx';
import { get } from 'lodash-es';
import { Tooltip } from '../index';

import * as React from 'react';

import { TruncatedContent } from './truncated-content';
import { CellParams, ColumnDefinition, RowModel } from './types';

export const TableCell = <TDataItem extends RowModel>({
  dataItem,
  dataItemIndex,
  column,
  expandability,
}: React.PropsWithChildren<
  {
    dataItem: TDataItem;
    dataItemIndex: number;
    column: ColumnDefinition<TDataItem>;
  } & Pick<CellParams<TDataItem>, 'expandability'>
>) => {
  const params: CellParams<TDataItem> = {
    row: dataItem,
    rowIndex: dataItemIndex,
    field: column.field,
    getValue: (field) => get(dataItem, field),
    value: get(dataItem, column.field),
    expandability,
  };
  const cellContent = column.renderCell
    ? column.renderCell(params)
    : get(dataItem, column.field);
  const description = column.description?.(params);
  const truncate = column.truncate ?? true;
  const renderContent = () => {
    if (truncate) {
      return description ? (
        <Tooltip label={description}>
          <div className='text-truncate'>{cellContent}</div>
        </Tooltip>
      ) : (
        <TruncatedContent>{cellContent}</TruncatedContent>
      );
    }
    // Previous behaviour: the content wraps, or scrolls inside the cell.
    const tdContent = <div>{cellContent}</div>;
    return description ? (
      <Tooltip label={description}>
        <div>{tdContent}</div>
      </Tooltip>
    ) : (
      tdContent
    );
  };
  return (
    <td
      className={clsx('table-cell text-truncate', column.tdClassName, {
        'wrap': !truncate,
        'w-100': column.flex,
        'text-center': column.align === 'center',
        'text-right': column.align === 'right',
        'text-left': column.align === 'left' || !column.align, // default to left
      })}
      {...(column.width && {
        style: {
          minWidth: column.width,
          maxWidth: column.width,
          width: column.width,
        },
      })}
      data-cy={`cell.${dataItemIndex}.${String(column.field)}`}
    >
      {renderContent()}
    </td>
  );
};
